import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, X } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { ConfirmModal } from '@/components/ConfirmModal';
import { formatAbsoluteTime } from '@/utils/date';

import type { AssetResponse } from '../../api';
import { PLATFORM_TO_CHANNEL } from '../../constants/taxonomy';
import type { BriefingRow } from '../briefing/api';
import type { AssetUrlLookup } from '../grid/useAssetUrls';
import { deleteBrief, getBrief } from '../../briefs/api';
import {
  describeBriefError,
  isDraftChanged,
  isDraftGone,
  saveDraft,
  saveIntoDraft,
  sendDraft,
  sendToProduction,
  type ProductionOrderRequest,
  type ProductionOrderResponse,
} from './api';
import { LineGroup } from './LineGroup';
import type { Basket, LinkedDraft } from './useBasket';

/**
 * Checkout, in two screens: review the order, then the confirmation.
 *
 * The order is grouped by media-plan row rather than listed flat, because the
 * row is what the work is delivered against — it carries the market, the size
 * and the deadline that production will actually build to. A flat list of
 * twelve assets hides the fact that they are twelve jobs for four different
 * markets.
 *
 * The confirmation is a screen, not a toast. Submitting reserves a brief
 * reference for the rows involved, so the user has just done something they
 * cannot take back from here, and the reference is worth more than the four
 * seconds a toast would give it.
 *
 * Saving a draft is the opposite: nothing is reserved and everything can still
 * change, so a toast is enough, and the saved rows leave the basket because
 * the draft is now where they live.
 *
 * A basket opened from a saved draft (`basket.draft`) writes back to it: its
 * market's rows update that draft and send it, rather than making a new one.
 * Rows of other markets become new briefs, one per market, as always.
 */

/**
 * Shared by saving and sending, so a drawer can tell that either is still
 * running — including one started by an earlier drawer that was closed
 * mid-request. The basket only empties on success, so without this, reopening
 * the drawer re-enables both buttons over the same lines and a second click
 * creates the brief twice.
 */
const CREATE_BRIEF_KEY = ['briefs', 'create'] as const;

/** One market's share of the basket, created as a brief of its own. */
interface MarketRequest {
  market: string;
  request: ProductionOrderRequest;
}

/** A brief sent to production, and the market it covers. */
type PlacedBrief = ProductionOrderResponse & { market: string };

/** A market whose brief couldn't be created, and why. */
interface FailedMarket {
  market: string;
  message: string;
}

/** What a Save or Send was asked to do. */
interface Checkout {
  requests: MarketRequest[];
  /** The draft to write the linked market's rows into; null makes new briefs. */
  linked: LinkedDraft | null;
  /** The linked draft's market has no rows left: delete it before going on. */
  deleteLinked?: boolean;
}

interface BasketDrawerProps {
  basket: Basket;
  /** Rows currently eligible to be briefed, from the Approved Rows panel. */
  rows: BriefingRow[];
  /** Every asset the basket refers to, resolved across tabs and reloads. */
  assetById: Map<string, AssetResponse>;
  urls: AssetUrlLookup;
  onClose: () => void;
  /**
   * The rows are still loading, or failed to. Until they are known every line
   * would read as no longer waiting, so nothing is judged and nothing acted
   * on -- a linked draft would otherwise look emptied and be offered for
   * deletion.
   */
  rowsLoading?: boolean;
  rowsFailed?: boolean;
}

export function BasketDrawer({
  basket,
  rows,
  assetById,
  urls,
  onClose,
  rowsLoading = false,
  rowsFailed = false,
}: BasketDrawerProps) {
  const rowsKnown = !rowsLoading && !rowsFailed;
  const { neededBy, setNeededBy, notes: brief, setNotes: setBrief, draft: linked } = basket;
  // A question to ask before going on: the linked draft's market has been
  // emptied, the linked draft is gone, or the user wants to stop editing it.
  const [asking, setAsking] = useState<
    | { kind: 'emptied'; action: 'save' | 'send'; requests: MarketRequest[] }
    | { kind: 'gone'; action: 'save' | 'send' }
    | { kind: 'changed' }
    | { kind: 'stop' }
    | null
  >(null);
  const [placed, setPlaced] = useState<{
    results: PlacedBrief[];
    failed: FailedMarket[];
  } | null>(null);
  const queryClient = useQueryClient();

  const rowById = new Map(rows.map((row) => [row.id, row]));

  /**
   * A row can leave `waiting_for_client_brief` while the basket is open —
   * someone else briefs it, or the same user does from Content Tracker — and it
   * then drops out of the panel this basket was filled from. Those lines are
   * shown and excluded rather than quietly sent: the backend would skip them
   * anyway, and finding out at the confirmation screen is worse than finding
   * out here.
   */
  const groups = Array.from(basket.byRow.entries()).map(([rowId, lines]) => ({
    rowId,
    row: rowById.get(rowId) ?? null,
    lines,
  }));
  const orderable = groups.filter((group) => group.row !== null);
  const stale = groups.filter((group) => group.row === null);
  const orderableCount = orderable.reduce((sum, group) => sum + group.lines.length, 0);

  /**
   * A brief is one channel's worth of work, and the backend refuses a mixed
   * one. The conflict is shown here, before either button is pressed, rather
   * than picked for the user — the agency briefed for Meta is not the one
   * briefed for Google.
   *
   * From the row's platform, not its `channel`: that is the campaign's free
   * label ("Social", "Paid Social"), while the backend derives a brief's
   * channel from platforms. Comparing labels would refuse two Meta rows the
   * backend accepts. An unmapped platform is left for the backend to judge.
   */
  const channels = Array.from(
    new Set(
      orderable
        .map((group) => PLATFORM_TO_CHANNEL[group.row!.platform.toLowerCase()])
        .filter((channel): channel is string => channel !== undefined),
    ),
  );
  const mixedChannels = channels.length > 1;

  /**
   * One request per market: a brief covers a single market, so that the
   * people of that market can be shown it without seeing another's rows. A
   * basket across Germany and Belgium becomes two briefs; the user picks as
   * before and never has to split it themselves.
   */
  const requestsByMarket = (): MarketRequest[] => {
    const byMarket = new Map<string, typeof orderable>();
    for (const group of orderable) {
      const market = group.row!.market;
      byMarket.set(market, [...(byMarket.get(market) ?? []), group]);
    }
    return Array.from(byMarket, ([market, groups]) => ({
      market,
      request: {
        rows: groups.map((group) => ({
          campaign_id: group.rowId,
          asset_ids: group.lines.map((line) => line.assetId),
        })),
        needed_by: neededBy || null,
        brief: brief.trim(),
      },
    }));
  };

  /**
   * Only the exact picks that were sent leave the basket. The drawer can be
   * closed mid-request and the user can keep adding to the same row; dropping
   * the whole row on success would throw away picks that were never sent.
   */
  const removeSent = (sent: ProductionOrderRequest, rowIds?: string[]) =>
    sent.rows
      .filter((row) => !rowIds || rowIds.includes(row.campaign_id))
      .forEach((row) =>
        row.asset_ids.forEach((assetId) => basket.remove(row.campaign_id, assetId)),
      );

  const errorMessage = (error: unknown, fallback: string) =>
    describeBriefError(error, (assetId) => assetById.get(assetId)?.filename_original, fallback);

  /**
   * Each market's brief is created on its own, one after another. One that
   * fails doesn't undo the others: what was created leaves the basket, and the
   * failed market's rows stay in it, named in the error, to try again.
   */
  const createPerMarket = async <T,>(
    { requests, linked: draftLink, deleteLinked }: Checkout,
    create: (request: ProductionOrderRequest) => Promise<T>,
    writeIntoDraft: (
      draft: LinkedDraft,
      request: ProductionOrderRequest,
      onSaved: (updatedAt: string) => void,
    ) => Promise<T>,
    fallback: string,
  ) => {
    // Its market emptied out, and the user chose to delete it. Already gone
    // counts as done.
    // A failure here stops everything before anything else is written: the
    // mutation's onError says so, and the basket and its link stay as they were.
    let deleted = false;
    if (draftLink && deleteLinked) {
      await deleteBrief(draftLink.id).catch((error: unknown) => {
        if (!isDraftGone(error)) throw error;
      });
      deleted = true;
    }
    const created: { market: string; request: ProductionOrderRequest; result: T }[] = [];
    const failed: { market: string; message: string }[] = [];
    // What stopped the linked draft being written: gone (sent or deleted), or
    // changed since the basket last saw it.
    let linkedProblem: 'gone' | 'changed' | null = null;
    for (const { market, request } of requests) {
      const intoDraft = draftLink && market === draftLink.market ? draftLink : null;
      try {
        created.push({
          market,
          request,
          result: await (intoDraft
            ? // The draft's new version is recorded the moment it is saved:
              // a submit that then fails must not leave the basket holding the
              // old one, or the retry is refused for the user's own save.
              writeIntoDraft(intoDraft, request, basket.touchDraft)
            : create(request)),
        });
      } catch (error) {
        // Sent or deleted since it was opened: its rows stay in the basket,
        // and the user is offered to make a new one from them instead.
        if (intoDraft && isDraftGone(error)) linkedProblem = 'gone';
        else if (intoDraft && isDraftChanged(error)) linkedProblem = 'changed';
        else failed.push({ market, message: errorMessage(error, fallback) });
      }
    }
    return {
      created,
      failed,
      linkedProblem,
      deleted,
      isSplit: requests.length > 1,
      updated: draftLink && created.some((c) => c.market === draftLink.market) ? draftLink : null,
    };
  };

  // Named by market only when there was more than one: a single-market
  // basket reads exactly as it did before the split.
  const reportFailures = (failed: { market: string; message: string }[], isSplit: boolean) => {
    for (const { market, message } of failed) {
      toast.error(isSplit ? `${market}: ${message}` : message);
    }
  };

  const draft = useMutation({
    mutationKey: CREATE_BRIEF_KEY,
    mutationFn: (checkout: Checkout) =>
      createPerMarket(checkout, saveDraft, saveIntoDraft, 'Could not save the draft'),
    // Only deleting an emptied draft throws out of here; every market's own
    // failure is caught and reported per market.
    onError: (error) => toast.error(errorMessage(error, 'Could not delete the draft')),
    onSuccess: ({ created, failed, linkedProblem, deleted, isSplit, updated }) => {
      if (deleted) {
        toast.success('Draft deleted');
        void queryClient.invalidateQueries({ queryKey: ['briefs'] });
      }
      created.forEach(({ request }) => removeSent(request));
      if (created.length > 0) {
        void queryClient.invalidateQueries({ queryKey: ['briefs'] });
        const made = created.filter((c) => c.market !== updated?.market).map((c) => c.market);
        toast.success(
          updated
            ? made.length > 0
              ? `Updated your ${updated.market} draft and saved ${made.length} new draft${made.length === 1 ? '' : 's'} (${made.join(', ')}).`
              : `Updated your ${updated.market} draft.`
            : made.length === 1
              ? 'Draft saved. Reopen it from My Briefs.'
              : `Saved ${made.length} drafts (${made.join(', ')}). Reopen them from My Briefs.`,
        );
      }
      reportFailures(failed, isSplit);
      // The linked draft has its rows now, whatever happened to the other
      // markets: let go of it, or a retry for those would find its market
      // empty and offer to delete the draft just saved.
      if (updated) basket.unlinkDraft();
      if (linkedProblem === 'gone') setAsking({ kind: 'gone', action: 'save' });
      else if (linkedProblem === 'changed') setAsking({ kind: 'changed' });
      else if (failed.length === 0) {
        // Written where it belongs: the basket is a fresh one again.
        basket.resetDetails();
        onClose();
      }
    },
  });

  const order = useMutation({
    mutationKey: CREATE_BRIEF_KEY,
    mutationFn: (checkout: Checkout) =>
      createPerMarket(checkout, sendToProduction, sendDraft, 'Could not send to production'),
    onError: (error) => toast.error(errorMessage(error, 'Could not delete the draft')),
    onSuccess: ({ created, failed, linkedProblem, deleted, isSplit, updated }) => {
      if (deleted) {
        toast.success('Draft deleted');
        void queryClient.invalidateQueries({ queryKey: ['briefs'] });
      }
      // The linked draft is done with only if its send ordered something. If
      // every row was skipped, the backend keeps it as a draft: stay linked,
      // with the date and notes, so fixing the cause and sending again goes
      // to that same draft rather than making a second one beside it.
      const linkedResult = updated
        ? created.find(({ market }) => market === updated.market)?.result
        : undefined;
      if (linkedResult && linkedResult.ordered.length > 0) basket.unlinkDraft();
      else if (linkedResult?.updatedAt) basket.touchDraft(linkedResult.updatedAt);
      if (linkedProblem) {
        // Ask before showing anything: the linked market's rows are still
        // here, and what to do with them is the user's call.
        created.forEach(({ request, result }) => removeSent(request, result.ordered));
        const sent = created.flatMap(({ market, result }) =>
          result.reference ? [`${result.reference} (${market})`] : [],
        );
        if (sent.length > 0) toast.success(`Sent ${sent.join(', ')}`);
        reportFailures(failed, isSplit);
        setAsking(
          linkedProblem === 'gone' ? { kind: 'gone', action: 'send' } : { kind: 'changed' },
        );
        return;
      }
      // A fresh basket only when everything went: nothing failed, and every
      // market ordered something.
      if (failed.length === 0 && created.every(({ result }) => result.ordered.length > 0)) {
        basket.resetDetails();
      }
      if (created.length > 0) {
        setPlaced({
          // A market that failed is listed on the confirmation, not only in a
          // toast: its rows are still waiting for a brief, and that has to
          // outlive four seconds as much as a skipped row does.
          failed,
          results: created.map(({ market, result }) => ({
            market,
            ...result,
            // `campaign_id` here is the row id (see ProductionOrderRow) --
            // resolve it back to the human-readable campaign code for display
            // where we still have the row (a skipped row that went stale
            // mid-basket may not).
            skipped: result.skipped.map((skip) => ({
              ...skip,
              campaign_id: rowById.get(skip.campaign_id)?.campaign_id ?? skip.campaign_id,
            })),
          })),
        });
        // Only drop rows that were actually ordered -- a skipped row (e.g. an
        // asset's rights expired between add and submit) is still unfinished
        // work and stays in the basket so the user can fix it and resubmit.
        created.forEach(({ request, result }) => removeSent(request, result.ordered));
        // Ordered rows are no longer waiting for a brief, so the panel they
        // came from has to drop them — otherwise the user can order the same
        // row twice for the next five minutes, which is how long the cached
        // list lives.
        void queryClient.invalidateQueries({ queryKey: ['asset-library', 'briefing-rows'] });
        void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        void queryClient.invalidateQueries({ queryKey: ['briefs'] });
      } else {
        // Nothing was created: the basket stays on screen, and the toast is
        // all there is to say.
        reportFailures(failed, isSplit);
      }
    },
  });

  const busy = useIsMutating({ mutationKey: CREATE_BRIEF_KEY }) > 0;
  const canAct = rowsKnown && orderableCount > 0 && !mixedChannels && !busy;
  // A linked draft can be saved with nothing left to order: that is how its
  // market is emptied out, and Save then asks whether to delete it.
  const canSave = rowsKnown && (orderableCount > 0 || linked !== null) && !mixedChannels && !busy;

  // The saved version replaces the basket, linked as it now stands.
  const loadLatest = async () => {
    if (!linked) return;
    try {
      const latest = await getBrief(linked.id);
      basket.load(
        {
          id: latest.id,
          market: latest.market ?? linked.market,
          createdAt: latest.created_at,
          updatedAt: latest.updated_at,
        },
        latest.lines.map((line) => ({ rowId: line.campaign_id, assetId: line.asset_id })),
        { neededBy: latest.needed_by ?? '', notes: latest.notes ?? '' },
      );
      toast.success('Loaded the latest version of your draft.');
    } catch {
      toast.error('Could not load the latest version of the draft');
    }
  };

  /**
   * Save or Send. With a linked draft whose market has no rows left -- all
   * removed, or none still waiting for a brief -- ask first whether to delete
   * it, rather than leave it untouched or remove it without a word.
   */
  const checkout = (action: 'save' | 'send', link: LinkedDraft | null = linked) => {
    const requests = requestsByMarket();
    if (link && !requests.some((request) => request.market === link.market)) {
      setAsking({ kind: 'emptied', action, requests });
      return;
    }
    (action === 'save' ? draft : order).mutate({ requests, linked: link });
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Click-outside closes during review. Once the order is placed the
          confirmation is the only record of the order number on screen, so it
          is dismissed deliberately. */}
      <button
        type="button"
        aria-label="Close basket"
        onClick={placed ? undefined : onClose}
        className="flex-1 bg-slate-900/30 backdrop-blur-[2px]"
      />

      <div className="flex h-full w-full max-w-[520px] flex-col border-l border-slate-200 bg-white shadow-2xl">
        {placed ? (
          <OrderPlaced results={placed.results} failed={placed.failed} onClose={onClose} />
        ) : (
          <>
            <header className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-[16px] font-bold text-slate-800">Send to production</h2>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
                  These assets go to the creative agency to be localised for each row&apos;s market
                  and placement.
                </p>
                {linked && (
                  <p
                    role="status"
                    className="mt-2 flex items-center gap-2 rounded-lg bg-[#0014cc]/5 px-2.5 py-1.5 text-[12px] text-[#0014cc]"
                  >
                    <span>
                      Editing draft · {linked.market} · created{' '}
                      {formatAbsoluteTime(linked.createdAt)}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setAsking({ kind: 'stop' })}
                      className="ml-auto font-medium underline underline-offset-2 disabled:text-slate-400"
                    >
                      Stop editing
                    </button>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="ml-auto rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="flex flex-col gap-4">
                {orderable.map(({ rowId, row, lines }) => (
                  <LineGroup
                    key={rowId}
                    market={row!.market}
                    title={row!.campaign_id}
                    details={`${row!.quarter} · ${row!.platform} · ${row!.format}${
                      row!.size ? ` · ${row!.size}` : ''
                    }`}
                    assetIds={lines.map((line) => line.assetId)}
                    assetById={assetById}
                    urls={urls}
                    onRemoveLine={(assetId) => basket.remove(rowId, assetId)}
                    onRemoveRow={() => basket.removeRow(rowId)}
                  />
                ))}

                {rowsFailed && (
                  <p
                    role="alert"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-[12px] text-amber-900"
                  >
                    Couldn’t check which rows are still waiting for a brief. Try again later.
                  </p>
                )}

                {rowsKnown && stale.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                    <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-900">
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                      {stale.length} row{stale.length === 1 ? '' : 's'} no longer waiting for a
                      brief
                    </p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-amber-800">
                      They have moved on since you added them — briefed by someone else, or
                      archived. Those lines won&apos;t be sent
                      {linked ? ', and saving removes them from this draft.' : '.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => stale.forEach((group) => basket.removeRow(group.rowId))}
                      className="mt-2 text-[12px] font-medium text-amber-900 underline underline-offset-2"
                    >
                      Remove them
                    </button>
                  </div>
                )}

                {mixedChannels && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
                    <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-red-900">
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                      This basket spans {channels.length} channels: {channels.join(', ')}
                    </p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-red-800">
                      A brief goes to one channel. Remove the rows for all but one channel to
                      continue.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-col gap-4 border-t border-slate-200 pt-5">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-semibold text-slate-700">Needed by</span>
                  <input
                    type="date"
                    value={neededBy}
                    onChange={(event) => setNeededBy(event.target.value)}
                    className="w-44 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] text-slate-700 focus:border-[#0014cc] focus:outline-none"
                  />
                  <span className="text-[11px] text-slate-400">
                    Optional. Sets the date the agency works to.
                  </span>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-[12px] font-semibold text-slate-700">
                    Localisation brief
                  </span>
                  <textarea
                    value={brief}
                    onChange={(event) => setBrief(event.target.value)}
                    rows={4}
                    placeholder="What the market needs applied — legal line, offer, endframe copy, language…"
                    className="resize-y rounded-lg border border-slate-200 px-2.5 py-2 text-[12.5px] leading-relaxed text-slate-700 placeholder:text-slate-400 focus:border-[#0014cc] focus:outline-none"
                  />
                  <span className="text-[11px] text-slate-400">
                    Applied to every row in this order.
                  </span>
                </label>
              </div>
            </div>

            <footer className="border-t border-slate-200 px-5 py-4">
              <div className="flex items-baseline justify-between">
                <span className="text-[12px] text-slate-500">Total</span>
                <span className="text-[13px] font-semibold text-slate-800">
                  {orderableCount} localisation{orderableCount === 1 ? '' : 's'} ·{' '}
                  {orderable.length} row{orderable.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={!canSave}
                  onClick={() => checkout('save')}
                  className="flex-1 rounded-lg border border-slate-200 bg-white py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                >
                  {draft.isPending ? 'Saving…' : linked ? 'Save draft' : 'Save as draft'}
                </button>
                <button
                  type="button"
                  disabled={!canAct}
                  onClick={() => checkout('send')}
                  className="flex-[2] rounded-lg bg-[#0014cc] py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                >
                  {order.isPending ? 'Sending…' : 'Send to production'}
                </button>
              </div>
              <p className="mt-2 text-center text-[11px] leading-relaxed text-slate-400">
                Sending creates a brief and reserves a reference number. A draft keeps your picks to
                finish later. Notifying the agency isn&apos;t wired up yet.
              </p>
            </footer>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={asking?.kind === 'emptied'}
        onClose={() => setAsking(null)}
        title={`Your ${linked?.market ?? ''} draft has nothing left to save`}
        message="Its assets were removed, or their rows are no longer waiting for a brief. Delete it, or keep it as it was last saved? The rest of the basket is saved either way."
        confirmText="Delete draft"
        variant="danger"
        secondaryText="Keep it as it was"
        onSecondary={() => {
          if (asking?.kind !== 'emptied') return;
          setAsking(null);
          // Kept: leave it alone, and make the rest new briefs.
          (asking.action === 'save' ? draft : order).mutate({
            requests: asking.requests,
            linked: null,
          });
        }}
        onConfirm={() => {
          if (asking?.kind !== 'emptied') return;
          setAsking(null);
          (asking.action === 'save' ? draft : order).mutate({
            requests: asking.requests,
            linked,
            deleteLinked: true,
          });
        }}
      />
      <ConfirmModal
        isOpen={asking?.kind === 'changed'}
        onClose={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null);
          void loadLatest();
        }}
        title="This draft changed since it went into your basket"
        message="Load the latest version? Changes in your basket that weren’t saved will be replaced."
        confirmText="Load latest"
        variant="warning"
      />
      <ConfirmModal
        isOpen={asking?.kind === 'gone'}
        onClose={() => setAsking(null)}
        title="This draft was sent or deleted in the meantime"
        message={
          asking?.kind === 'gone' && asking.action === 'send'
            ? 'Send your picks as a new brief instead?'
            : 'Save your picks as a new draft instead?'
        }
        confirmText={
          asking?.kind === 'gone' && asking.action === 'send' ? 'Send as new' : 'Save as new'
        }
        variant="warning"
        onConfirm={() => {
          if (asking?.kind !== 'gone') return;
          setAsking(null);
          basket.unlinkDraft();
          checkout(asking.action, null);
        }}
      />
      <ConfirmModal
        isOpen={asking?.kind === 'stop'}
        onClose={() => setAsking(null)}
        title="Discard your changes to this draft?"
        message="The draft stays as it was last saved, and the basket is emptied."
        confirmText="Discard"
        variant="warning"
        onConfirm={() => {
          setAsking(null);
          basket.clear();
          onClose();
        }}
      />
    </div>
  );
}

/**
 * The confirmation: order numbers, what moved, and what happens next. One
 * brief per market the basket covered, each with its own reference.
 */
function OrderPlaced({
  results,
  failed,
  onClose,
}: {
  results: PlacedBrief[];
  /** Markets of the same basket whose brief couldn't be created. */
  failed: FailedMarket[];
  onClose: () => void;
}) {
  const marketCount = results.length + failed.length;
  const isSplit = marketCount > 1;
  // Submitted means given a reference. A market whose every row was skipped
  // came back without one: the backend kept it as a draft, so it counts as not
  // sent, alongside the markets whose request failed outright.
  const withReference = results.filter((result) => result.reference);
  const keptAsDraft = results.filter((result) => !result.reference);
  const notSentCount = failed.length + keptAsDraft.length;
  const orderedRows = results.reduce((sum, result) => sum + result.ordered.length, 0);
  const skipped = results.flatMap((result) =>
    result.skipped.map((skip) => ({ ...skip, market: result.market })),
  );
  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        {withReference.length > 0 ? (
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0014cc]/10">
            <Check className="h-6 w-6 text-[#0014cc]" strokeWidth={2.5} />
          </span>
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-700" strokeWidth={2} />
          </span>
        )}
        <h2 className="mt-4 text-[19px] font-bold text-slate-800">
          {withReference.length === 0
            ? 'Nothing was submitted'
            : !isSplit
              ? 'Brief submitted'
              : notSentCount > 0
                ? `${withReference.length} of ${marketCount} briefs submitted`
                : `${withReference.length} briefs submitted`}
        </h2>
        {withReference.length > 0 && (
          <ul className="mt-1.5 flex flex-col items-center gap-0.5">
            {withReference.map((result) => (
              <li
                key={result.reference}
                className="font-mono text-[13px] font-semibold tracking-wide text-[#0014cc]"
              >
                {result.reference}
                {isSplit && (
                  <span className="font-sans font-medium text-slate-500"> · {result.market}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 max-w-[38ch] text-[12.5px] leading-relaxed text-slate-500">
          {orderedRows > 0
            ? `${orderedRows} row${orderedRows === 1 ? '' : 's'} reserved a brief reference.`
            : 'No rows were submittable — see below.'}
          {isSplit && ' One brief per market, so each is seen by its own market.'}
        </p>

        {notSentCount > 0 && (
          <div
            role="alert"
            className="mt-4 w-full rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-left"
          >
            <p className="text-[12px] font-semibold text-amber-900">
              {isSplit
                ? `${notSentCount} market${notSentCount === 1 ? '' : 's'} not sent`
                : 'Not sent'}
            </p>
            <ul className="mt-1.5 flex flex-col gap-1">
              {keptAsDraft.map(({ market }) => (
                <li key={market} className="text-[11.5px] leading-relaxed text-amber-800">
                  {/* Named only when the basket was split, as everywhere else:
                      with one market there is nothing to tell apart. */}
                  {isSplit ? (
                    <>
                      <span className="font-semibold">{market}</span>: nothing
                    </>
                  ) : (
                    'Nothing'
                  )}{' '}
                  could be sent. Kept as a draft with the reasons in My Briefs; its rows are still
                  in your basket.
                </li>
              ))}
              {failed.map(({ market, message }) => (
                <li key={market} className="text-[11.5px] leading-relaxed text-amber-800">
                  <span className="font-semibold">{market}</span>: not sent —{' '}
                  {message.replace(/\.$/, '')}. Its rows are still in your basket.
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Partial success is stated here rather than in a toast: a skipped row
            is a job nobody is doing, and it needs to outlive four seconds. */}
        {skipped.length > 0 && (
          <div className="mt-4 w-full rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-left">
            <p className="text-[12px] font-semibold text-amber-900">
              {skipped.length} row{skipped.length === 1 ? '' : 's'} skipped
            </p>
            <ul className="mt-1.5 flex flex-col gap-1">
              {skipped.map((skip) => (
                <li
                  key={`${skip.market}-${skip.campaign_id}`}
                  className="text-[11.5px] leading-relaxed text-amber-800"
                >
                  <span className="font-mono">{skip.campaign_id}</span>
                  {isSplit && ` (${skip.market})`} — {skip.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ol className="mt-6 flex w-full flex-col gap-2.5 border-t border-slate-200 pt-5 text-left">
          {[
            ...(withReference.length > 0
              ? ['A brief reference has been reserved for the rows that were submittable.']
              : []),
            "Rows with an issue (like an expired asset) are listed above and weren't included.",
            "Notifying the agency isn't wired up yet — that part is still being built.",
          ].map((step, index) => (
            <li key={step} className="flex gap-2.5">
              <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-500">
                {index + 1}
              </span>
              <span className="text-[12px] leading-relaxed text-slate-600">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <footer className="border-t border-slate-200 px-5 py-4">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-lg bg-slate-800 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-slate-900"
        >
          Back to the library
        </button>
      </footer>
    </>
  );
}
